'use client';

import * as React from 'react';
import { FiX, FiXCircle } from 'react-icons/fi';
import { IoCheckmarkCircleOutline } from 'react-icons/io5';
import * as ToastPrimitive from '@radix-ui/react-toast';

import { cn } from '~/shared/utils/cn';

export type ToastVariant = 'success' | 'error' | 'warning' | 'neutral';

type ToastProps = {
  title: string;
  isDetailed?: boolean;
  description?: string;
  iconSize?: number;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  closeAriaLabel?: string;
  variant?: ToastVariant;
  titleClassName?: string;
  descriptionClassName?: string;
} & Omit<
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Root>,
  'title' | 'children'
>;

const rootVariantClasses: Record<ToastVariant, string> = {
  success: 'bg-green-300',
  error: 'bg-red-100',
  warning: 'bg-orange-100',
  neutral: 'bg-gray-450',
};

const detailedRootVariantClasses: Record<ToastVariant, string> = {
  success: 'bg-green-400',
  error: 'bg-red-50',
  warning: 'bg-orange-100',
  neutral: 'bg-gray-450',
};

const titleColorClasses: Record<ToastVariant, string> = {
  success: 'text-green-900',
  error: 'text-red-700',
  warning: 'text-orange-500',
  neutral: 'text-gray-750',
};

const baseRootClasses = [
  'relative flex items-center justify-center rounded p-4',
  'data-[state=open]:animate-toast-slide-in',
  'data-[state=closed]:animate-toast-hide',
  'data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]',
  'data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:transition-transform data-[swipe=cancel]:duration-200',
  'data-[swipe=end]:animate-toast-swipe-out',
].join(' ');

function ToastIcon({
  icon,
  iconSize = 24,
  variant = 'success',
}: Pick<ToastProps, 'iconSize' | 'icon' | 'variant'>) {
  const iconByVariant: Record<ToastVariant, React.ReactNode> = {
    success: <IoCheckmarkCircleOutline size={iconSize} />,
    warning: <IoCheckmarkCircleOutline size={iconSize} />,
    error: <FiXCircle size={iconSize} />,
    neutral: <IoCheckmarkCircleOutline size={iconSize} />,
  };

  return (
    <div style={{ width: iconSize, height: iconSize }}>
      {icon || iconByVariant[variant]}
    </div>
  );
}

function RawToast({
  title,
  closeAriaLabel,
  isDetailed = false,
  variant = 'success',
  description,
  iconSize = 24,
  children,
  icon,
  titleClassName,
  descriptionClassName,
  className,
  ...props
}: ToastProps) {
  if (isDetailed) {
    return (
      <ToastPrimitive.Root
        {...props}
        className={cn(
          baseRootClasses,
          detailedRootVariantClasses[variant],
          'justify-between gap-4 lg:min-w-[32rem]',
          className
        )}
      >
        <ToastIcon icon={icon} iconSize={iconSize} variant={variant} />
        <div className="flex flex-auto flex-col">
          <ToastPrimitive.Title
            className={cn(
              'font-bold text-lg',
              titleColorClasses[variant],
              titleClassName
            )}
          >
            {title}
          </ToastPrimitive.Title>
          {description && (
            <ToastPrimitive.Description
              className={cn(
                'font-normal text-sm text-black',
                descriptionClassName
              )}
            >
              {description}
            </ToastPrimitive.Description>
          )}
          {children}
        </div>
        <ToastPrimitive.Close
          aria-label={closeAriaLabel}
          className="flex cursor-pointer border-0 bg-transparent text-xs text-gray-750"
        >
          <FiX />
        </ToastPrimitive.Close>
      </ToastPrimitive.Root>
    );
  }

  return (
    <ToastPrimitive.Root
      {...props}
      className={cn(baseRootClasses, rootVariantClasses[variant], className)}
    >
      <ToastPrimitive.Title
        className={cn(
          'font-bold text-xs',
          titleColorClasses[variant],
          titleClassName
        )}
      >
        {title}
      </ToastPrimitive.Title>

      <ToastPrimitive.Close
        aria-label={closeAriaLabel}
        className="absolute top-1 right-1 flex cursor-pointer border-0 bg-transparent text-xs text-gray-750"
      >
        <FiX />
      </ToastPrimitive.Close>
    </ToastPrimitive.Root>
  );
}

const baseViewportClasses =
  'fixed top-0 left-1/2 z-[2147483647] m-0 flex max-w-screen -translate-x-1/2 list-none flex-col gap-4 p-6 outline-none';

const Viewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Viewport
    ref={ref}
    {...props}
    className={cn(baseViewportClasses, className)}
  />
));
Viewport.displayName = 'ToastViewport';

const DetailedViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Viewport
    ref={ref}
    {...props}
    className={cn(
      'fixed bottom-0 right-0 z-[2147483647] m-0 flex max-w-screen list-none flex-col gap-4 p-6 outline-none',
      className
    )}
  />
));
DetailedViewport.displayName = 'ToastDetailedViewport';

export const Toast = Object.assign(RawToast, {
  Provider: ToastPrimitive.Provider,
  Viewport,
  DetailedViewport,
  Close: ToastPrimitive.Close,
});
